import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, Phone } from "lucide-react";
import { useRef } from "react";
import { scrollToId } from "@/lib/scroll";
import { trackClick } from "@/lib/api";
import { useContact } from "@/lib/contact";

const EASE = [0.22, 1, 0.36, 1];
const CX = 600;
const CY = 380;
const ISO = 0.866;
const P = (x, y, z = 0) => [CX + (x - y) * ISO, CY - z + (x + y) * 0.5];
const pl = (...ps) => ps.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");

const WireBox = ({ x, y, w, d, h, stroke = "rgba(255,255,255,0.09)", sw = 1.2 }) => {
    const a = w / 2;
    const b = d / 2;
    const t1 = P(x - a, y - b, h);
    const t2 = P(x + a, y - b, h);
    const t3 = P(x + a, y + b, h);
    const t4 = P(x - a, y + b, h);
    return (
        <g fill="none" stroke={stroke} strokeWidth={sw}>
            <polygon points={pl(t1, t2, t3, t4)} />
            <line x1={t3[0]} y1={t3[1]} x2={t3[0]} y2={t3[1] + h} />
            <line x1={t2[0]} y1={t2[1]} x2={t2[0]} y2={t2[1] + h} />
            <line x1={t4[0]} y1={t4[1]} x2={t4[0]} y2={t4[1] + h} />
            <line x1={t4[0]} y1={t4[1] + h} x2={t3[0]} y2={t3[1] + h} />
            <line x1={t3[0]} y1={t3[1] + h} x2={t2[0]} y2={t2[1] + h} />
        </g>
    );
};

const rise = (delay) => ({
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.75, ease: EASE, delay },
});

export default function FinalCtaSection() {
    const contact = useContact();
    const reduced = useReducedMotion();
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 50, damping: 18, mass: 0.7 });
    const sy = useSpring(my, { stiffness: 50, damping: 18, mass: 0.7 });
    const gridX = useTransform(sx, [-1, 1], [2, -2]);
    const gridY = useTransform(sy, [-1, 1], [1.5, -1.5]);
    const wireX = useTransform(sx, [-1, 1], [4, -4]);
    const wireY = useTransform(sy, [-1, 1], [3, -3]);
    const glowX = useTransform(sx, [-1, 1], [5, -5]);
    const glowY = useTransform(sy, [-1, 1], [4, -4]);

    const onMouseMove = (e) => {
        if (reduced) return;
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        mx.set(((e.clientX - rect.left) / rect.width) * 2 - 1);
        my.set(((e.clientY - rect.top) / rect.height) * 2 - 1);
    };

    return (
        <section
            ref={ref}
            onMouseMove={onMouseMove}
            onMouseLeave={() => {
                mx.set(0);
                my.set(0);
            }}
            data-testid="final-cta-section"
            className="cv-auto relative overflow-hidden bg-[#0B1E30] py-24 sm:py-36"
        >
            {/* grid layer */}
            <motion.div style={{ x: gridX, y: gridY }} aria-hidden="true" className="absolute inset-0">
                <svg className="h-full w-full" fill="none">
                    <defs>
                        <pattern id="ctaGrid" width="52" height="52" patternUnits="userSpaceOnUse">
                            <path d="M52 0H0V52" stroke="#FFFFFF" strokeOpacity="0.03" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#ctaGrid)" />
                </svg>
            </motion.div>

            {/* wireframe workspace layer */}
            <motion.div style={{ x: wireX, y: wireY }} aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
                <motion.svg
                    viewBox="0 0 1200 600"
                    className="h-auto w-[130%] max-w-none sm:w-[110%] lg:w-[1000px]"
                    fill="none"
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-100px" }}
                >
                    <defs>
                        <linearGradient id="ctaLineGrad" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0" stopColor="#7E35A8" />
                            <stop offset="0.5" stopColor="#2B5CAB" />
                            <stop offset="1" stopColor="#22B3A3" />
                        </linearGradient>
                    </defs>
                    <motion.g
                        variants={{
                            hidden: { opacity: 0, y: 16 },
                            show: { opacity: 1, y: 0, transition: { duration: 1.2, ease: EASE } },
                        }}
                    >
                        {/* floor slab */}
                        <WireBox x={0} y={0} w={560} d={400} h={22} stroke="rgba(255,255,255,0.1)" sw={1.4} />
                        {/* coworking desks */}
                        <WireBox x={-120} y={40} w={190} d={32} h={72} />
                        <WireBox x={-120} y={120} w={190} d={32} h={72} />
                        {/* meeting table */}
                        <WireBox x={170} y={-30} w={160} d={66} h={70} />
                        {/* cabin glass L */}
                        <polygon points={pl(P(-270, -60, 22), P(-110, -60, 22), P(-110, -60, 140), P(-270, -60, 140))} stroke="rgba(160,190,235,0.16)" strokeWidth={1.2} />
                        <polygon points={pl(P(-110, -160, 22), P(-110, -60, 22), P(-110, -60, 140), P(-110, -160, 140))} stroke="rgba(160,190,235,0.12)" strokeWidth={1.2} />
                        {/* pendants */}
                        {[130, 210].map((px) => (
                            <g key={px} stroke="rgba(255,255,255,0.14)" strokeWidth={1.1}>
                                <line x1={P(px, -30, 170)[0]} y1={P(px, -30, 170)[1]} x2={P(px, -30, 128)[0]} y2={P(px, -30, 128)[1]} />
                                <circle cx={P(px, -30, 124)[0]} cy={P(px, -30, 124)[1]} r={4} fill="rgba(255,216,150,0.35)" stroke="none" />
                            </g>
                        ))}
                        {/* signature gradient floor line */}
                        <motion.line
                            x1={P(-270, 190, 23)[0]}
                            y1={P(-270, 190, 23)[1]}
                            x2={P(270, 190, 23)[0]}
                            y2={P(270, 190, 23)[1]}
                            stroke="url(#ctaLineGrad)"
                            strokeWidth={1.6}
                            strokeOpacity={0.55}
                            variants={{
                                hidden: { pathLength: 0, opacity: 0 },
                                show: { pathLength: 1, opacity: 1, transition: { duration: 1.6, ease: EASE, delay: 0.5 } },
                            }}
                        />
                    </motion.g>
                </motion.svg>
            </motion.div>

            {/* glow layer — slow ambient drift + cursor response */}
            <motion.div style={{ x: glowX, y: glowY }} aria-hidden="true" className="pointer-events-none absolute inset-0">
                <motion.div
                    animate={reduced ? {} : { x: [0, 34, 0], y: [0, -22, 0] }}
                    transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute left-[12%] top-[8%] h-[420px] w-[420px] rounded-full opacity-[0.13]"
                    style={{ background: "radial-gradient(circle, #7E35A8 0%, transparent 65%)" }}
                />
                <motion.div
                    animate={reduced ? {} : { x: [0, -30, 0], y: [0, 20, 0] }}
                    transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute bottom-[-10%] right-[8%] h-[400px] w-[400px] rounded-full opacity-[0.12]"
                    style={{ background: "radial-gradient(circle, #22B3A3 0%, transparent 65%)" }}
                />
                <div
                    className="absolute left-1/2 top-1/2 h-[360px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.09]"
                    style={{ background: "radial-gradient(circle, #2B5CAB 0%, transparent 65%)" }}
                />
            </motion.div>

            {/* content */}
            <div className="relative z-10 mx-auto max-w-3xl px-5 text-center sm:px-8">
                <motion.p
                    {...rise(0.1)}
                    data-testid="final-cta-eyebrow"
                    className="text-[11px] font-bold uppercase tracking-[0.26em] text-gradient-dark"
                >
                    Ready to Make the Move?
                </motion.p>
                <motion.h2
                    {...rise(0.2)}
                    className="mt-5 text-4xl font-bold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl"
                >
                    Ready to <span className="text-gradient-dark">Work Better?</span>
                </motion.h2>
                <motion.p
                    {...rise(0.3)}
                    className="mx-auto mt-5 max-w-xl text-sm font-medium leading-relaxed text-slate-400 md:text-base"
                >
                    Experience Work Island, explore your workspace options and find the monthly
                    plan that fits the way you work.
                </motion.p>

                <motion.div {...rise(0.4)} className="mt-9 flex flex-col items-center justify-center gap-3.5 sm:flex-row">
                    <button
                        data-testid="final-cta-book-demo"
                        onClick={() => scrollToId("#book-demo")}
                        className="btn-gradient group inline-flex w-full items-center justify-center gap-2.5 rounded-full px-8 py-4 text-[15px] font-bold text-white sm:w-auto"
                    >
                        Book a Free Demo
                        <ArrowRight size={17} strokeWidth={2.6} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                    <button
                        data-testid="final-cta-view-plans"
                        onClick={() => scrollToId("#pricing")}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-8 py-4 text-[15px] font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-white/35 hover:bg-white/10 active:scale-[0.98] sm:w-auto"
                    >
                        View Plans
                    </button>
                </motion.div>

                <motion.p {...rise(0.5)} className="mt-5 text-[12.5px] font-medium text-slate-500">
                    Experience the workspace before choosing your monthly plan.
                </motion.p>

                <motion.div {...rise(0.55)} className="mt-7 flex items-center justify-center gap-5">
                    <span className="text-[12.5px] font-semibold text-slate-500">Prefer to talk first?</span>
                    <a
                        data-testid="final-cta-call"
                        href={contact.telUrl}
                        onClick={() => trackClick("call", "final-cta")}
                        className="group inline-flex items-center gap-2 text-[13px] font-bold text-slate-300 transition-colors hover:text-white"
                    >
                        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-[#5B8FE0] transition-transform duration-300 group-hover:-translate-y-0.5">
                            <Phone size={14} strokeWidth={2.4} />
                        </span>
                        Call Now
                    </a>
                    <span className="h-4 w-px bg-white/15" aria-hidden="true" />
                    <a
                        data-testid="final-cta-whatsapp"
                        href={contact.waLink("Hi Work Island, I'd like to book a free demo!")}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackClick("whatsapp", "final-cta")}
                        className="group inline-flex items-center gap-2 text-[13px] font-bold text-slate-300 transition-colors hover:text-white"
                    >
                        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-brand-teal transition-transform duration-300 group-hover:-translate-y-0.5">
                            <MessageCircle size={14} strokeWidth={2.4} />
                        </span>
                        WhatsApp Us
                    </a>
                </motion.div>
            </div>

            {/* hairline transition into footer */}
            <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-px"
                style={{ background: "linear-gradient(90deg, transparent, rgba(126,58,168,0.4), rgba(43,92,171,0.4), rgba(34,179,163,0.4), transparent)" }}
            />
        </section>
    );
}
